import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { ExternalLink } from "react-feather";
import Select, { Option } from "@cs/components/src/Select";
import Button from "@cs/components/src/Button";
import Input from "@cs/components/src/Input";
import Banner from "@cs/components/src/Banner";
import Section from "@cs/components/src/Section";
import ClickOutside from "@cs/components/src/ClickOutside";
import ApiService from "../../services/ApiService";

const EXTENSION_ID = process.env.REACT_APP_ICONIK_EXTENSION_ID;
const CREDS_KEY = "iconik_launch_creds";

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 10px;
`;

const SelectWrapper = styled.div`
  min-width: 250px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

interface LaunchData {
  token: string;
  appId: string;
  systemDomainId: string;
  domainName: string;
  baseUrl: string;
  userId?: string;
}

interface Creds {
  email: string;
  password: string;
}

const chromeRuntime = () => (window as any).chrome?.runtime;

const loadCreds = (): Creds | null => {
  try {
    const raw = sessionStorage.getItem(CREDS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveCreds = (creds: Creds) =>
  sessionStorage.setItem(CREDS_KEY, JSON.stringify(creds));

const clearCreds = () => sessionStorage.removeItem(CREDS_KEY);

const SelectOrganization = () => {
  const { systemDomainId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [extensionReady, setExtensionReady] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const pingRef = useRef(false);

  useEffect(() => {
    if (!EXTENSION_ID || pingRef.current) {
      return;
    }
    pingRef.current = true;
    const rt = chromeRuntime();
    if (!rt) {
      return;
    }
    rt.sendMessage(EXTENSION_ID, { type: "PING" }, (response: any) => {
      if (!rt.lastError && response?.ok) {
        setExtensionReady(true);
      }
    });
  }, []);

  const launch = async (creds: Creds) => {
    if (!systemDomainId || !EXTENSION_ID) {
      return;
    }
    setLaunching(true);
    setError(null);
    try {
      const { data } = await ApiService.fetch<{ data: LaunchData }>(
        "POST",
        `/api/config/organization/${systemDomainId}/launch`,
        creds
      );
      saveCreds(creds);
      setShowModal(false);
      chromeRuntime().sendMessage(EXTENSION_ID, {
        type: "ICONIK_LAUNCH",
        ...data,
      });
    } catch (e: any) {
      if (e.message === "Invalid iconik credentials") {
        clearCreds();
        setError("Wrong email or password.");
      } else {
        setError(e.message ?? "Something went wrong. Try again.");
      }
    } finally {
      setLaunching(false);
    }
  };

  const handleLaunchClick = () => {
    const creds = loadCreds();
    if (creds) {
      launch(creds);
    } else {
      setEmail("");
      setPassword("");
      setError(null);
      setShowModal(true);
    }
  };

  return (
    <Wrapper>
      <SelectWrapper>
        <Select
          placeholder="Select an organization"
          value={systemDomainId}
          onChange={({ value }) => {
            navigate(
              location.pathname
                .replace(/dashboard\/[^/]*\//, `dashboard/${value}/`)
                .replace(/plugin\/.*/, "plugin")
            );
          }}
          optionsAsync={{
            key: `_/organization`,
            fn: async () =>
              (
                await ApiService.fetch<{ data: Option[] }>(
                  "GET",
                  `/api/select_option/_/organization`
                )
              ).data,
          }}
        ></Select>
      </SelectWrapper>

      {extensionReady && systemDomainId && (
        <Button
          icon={ExternalLink}
          label="Open in Iconik"
          type="default"
          onClick={handleLaunchClick}
          loading={launching}
          disabled={launching}
        />
      )}

      {showModal && (
        <ModalOverlay>
          <ClickOutside onClickOutside={() => setShowModal(false)}>
            <Section
              modal
              title="Sign in to Iconik"
              onClose={() => setShowModal(false)}
              width="480px"
              pad
              footer={
                <ModalButtons>
                  <Button
                    label="Cancel"
                    type="danger"
                    onClick={() => setShowModal(false)}
                    disabled={launching}
                  />
                  <Button
                    label="Open in Iconik"
                    type="success"
                    icon={ExternalLink}
                    onClick={() => {
                      if (email && password) {
                        launch({ email, password });
                      }
                    }}
                    loading={launching}
                    disabled={!email || !password || launching}
                  />
                </ModalButtons>
              }
            >
              <Input
                label="Email"
                value={email}
                onChange={setEmail}
                onSubmit={() => {
                  if (email && password) {
                    launch({ email, password });
                  }
                }}
              />
              <Input
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                onSubmit={() => {
                  if (email && password) {
                    launch({ email, password });
                  }
                }}
              />
              {error && <Banner error={error} />}
            </Section>
          </ClickOutside>
        </ModalOverlay>
      )}
    </Wrapper>
  );
};

export default SelectOrganization;
